/**
 * Owner: Masa Bayu
 * File: PrivateAuthorMobileBooks.tsx
 * Date:Time: 2026-10-23 09:30
 * Description: Paparan Buku Saya mudah alih PRIVAT §467-C — kepala kembali + sub,
 *   pil penapis keadaan SEBENAR hub (Semua / Dijual / Tiada Harga, kiraan dari
 *   `model.books`), carian judul, baris buku milik akaun (cover, kategori, harga
 *   hub atau "harga belum ditetapkan", ⋮ + › → /publication), kad CTA muat naik
 *   dan FAB — semua laluan hidup.
 *   Dibuang bersama dataset demo: katalog pasaran penuh yang disalah-pam sebagai
 *   buku saya, pil Draf/Ditolak (tiada keadaan sedemikian di hub) dan baris
 *   pengarang karangan.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { LandingIcon } from '../../home/marketplace/landing/LandingIcons';
import { qxCategoryName } from '@qx/qxread-core';
import { resolveMarketplaceLabels } from '../../home/marketplace/marketplace-locale';
import type { MarketplaceLocaleProfile } from '../../home/marketplace/marketplace-locale';
import type { PrivateAuthorLabels } from './private-author-labels';
import type { AuthorDashboardModel } from './private-author-view';
import { removeOwnedPublication } from './publisher-dashboard';
import type {
  PrivateAuthorMobileLabels,
  PrivateMobilePill,
  PrivateMobileViewId,
} from './private-author-mobile-labels';
import { MobileSubHeader } from './PrivateAuthorMobileSubHeader';

const TONES = ['#dbeafe', '#fde8e8', '#e7f6ef', '#fef3c7', '#f3e8ff'];
const PILL_ORDER: readonly PrivateMobilePill[] = ['all', 'live', 'unpriced'];

export interface PrivateAuthorMobileBooksProps {
  readonly onNavigate: (path: string) => void;
  readonly onBack: () => void;
  readonly openView: (view: PrivateMobileViewId) => void;
  readonly showToast: (message: string) => void;
  readonly profile: MarketplaceLocaleProfile;
  readonly la: PrivateAuthorLabels;
  readonly ml: PrivateAuthorMobileLabels;
  readonly model: AuthorDashboardModel;
}

export function PrivateAuthorMobileBooks({
  onNavigate,
  onBack,
  openView,
  showToast,
  profile,
  la,
  ml,
  model,
}: PrivateAuthorMobileBooksProps): ReactElement {
  const t = resolveMarketplaceLabels(profile);
  const [pill, setPill] = useState<PrivateMobilePill>('all');
  const [query, setQuery] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const removeBook = (publicationId: string, title: string): void => {
    const question = la.bookMenuRemoveConfirm.replace('{title}', title);
    if (!window.confirm(question)) return;
    setNotice(null);
    void removeOwnedPublication(publicationId).then((result) => {
      if (!result.ok) setNotice(result.error);
    });
  };

  const liveCount = model.books.filter((book) => book.listed).length;
  const counts: Record<PrivateMobilePill, number> = {
    all: model.books.length,
    live: liveCount,
    unpriced: model.books.length - liveCount,
  };

  const needle = query.trim().toLowerCase();
  const visible = model.books.filter((book) => {
    if (pill === 'live' && !book.listed) {
      return false;
    }
    if (pill === 'unpriced' && book.listed) {
      return false;
    }
    return needle === '' || book.title.toLowerCase().includes(needle);
  });

  const emptyText =
    needle !== ''
      ? ml.emptySearch
      : pill === 'live'
        ? ml.emptyLive
        : pill === 'unpriced'
          ? ml.emptyUnpriced
          : la.booksEmpty;

  const openDetail = (publicationId: string): void => {
    onNavigate(`/publication/${encodeURIComponent(publicationId)}`);
  };

  return (
    <>
      <MobileSubHeader
        title={ml.bottom.buku}
        sub={ml.booksSub}
        onBack={onBack}
        backAria={ml.backAria}
      />
      <div className="qxpm__pills" role="tablist" aria-label={ml.bottom.buku}>
        {PILL_ORDER.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={pill === id}
            className={`qxpm__pill${pill === id ? ' qxpm__pill--on' : ''}`}
            onClick={() => {
              setPill(id);
            }}
          >
            {`${ml.pills[id]} (${String(counts[id])})`}
          </button>
        ))}
      </div>
      <div className="qxpm__booktools">
        <input
          type="search"
          className="qxpm__bsearch"
          value={query}
          placeholder={ml.booksSearchPlaceholder}
          aria-label={ml.booksSearchAria}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
        <button
          type="button"
          className="qxpm__funnel"
          aria-label={ml.filterAria}
          onClick={() => {
            showToast(ml.filterToast);
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M4 5h16l-6 8v5l-4 2v-7z" />
          </svg>
        </button>
      </div>

      <ul className="qxpm__rows" role="list">
        {visible.map((book) => (
          <li key={book.publicationId}>
            <div className="qxpm__row">
              <span className="qxpm__rowmenu">
                <button
                  type="button"
                  className="qxpm__dots"
                  aria-label={`${ml.rowMenuAria} — ${book.title}`}
                  onClick={() => {
                    openDetail(book.publicationId);
                  }}
                >
                  <LandingIcon name="more" size={16} />
                </button>
              </span>
              <button
                type="button"
                className="qxpm__row-cover"
                style={{ background: TONES[book.coverTone % TONES.length] ?? '#dbeafe' }}
                aria-label={`${ml.rowMenuAria}: ${book.title}`}
                onClick={() => {
                  openDetail(book.publicationId);
                }}
              >
                <span>{book.title}</span>
              </button>
              <div className="qxpm__row-main">
                <p className="qxpm__row-title">{book.title}</p>
                {book.category === null ? null : (
                  <em className="qxpm__row-genre">{qxCategoryName(book.category, t.language)}</em>
                )}
                <p className="qxpm__row-meta">
                  <strong>{book.priceText}</strong>
                  {book.soldOrders === 0 ? null : (
                    <span className="qxpm__row-sold">
                      {`${String(book.soldOrders)} ${la.soldSuffix}`}
                    </span>
                  )}
                </p>
                <span className={`qxpm__row-status${book.listed ? '' : ' qxpm__row-status--off'}`}>
                  {book.listed ? la.status.published : la.status.unlisted}
                </span>
                <button
                  type="button"
                  className="qxpm__row-open"
                  aria-label={`${la.bookMenuRemove} — ${book.title}`}
                  onClick={() => {
                    removeBook(book.publicationId, book.title);
                  }}
                >
                  {la.bookMenuRemove}
                </button>
              </div>
              <button
                type="button"
                className="qxpm__row-open"
                aria-label={`${ml.statDetail}: ${book.title}`}
                onClick={() => {
                  openDetail(book.publicationId);
                }}
              >
                <LandingIcon name="chevronRight" size={12} />
              </button>
            </div>
          </li>
        ))}
        {visible.length === 0 ? <li className="qxpm__row-empty">{emptyText}</li> : null}
        {notice === null ? null : (
          <li className="qxpm__row-empty" role="alert">
            {notice}
          </li>
        )}
      </ul>

      <section className="qxpm__card qxpm__cta">
        <p>{ml.ctaTitle}</p>
        <button
          type="button"
          className="qxpm__cta-btn"
          onClick={() => {
            openView('penerbitan');
          }}
        >
          {ml.ctaButton}
        </button>
      </section>
      <button
        type="button"
        className="qxpm__fab qx-motion-press"
        aria-label={ml.fabAria}
        onClick={() => {
          openView('penerbitan');
        }}
      >
        +
      </button>
    </>
  );
}
